import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { API, shopifyStatus, shopifyOAuthStart, shopifySync, shopifyDisconnect, shopifyVerifyScopes, shopifyReauthorize, shopifyData, shopifyPropose, shopifyWritePropose, shopifyWriteActivity, fmtMoney, fmtNum, fmtPct } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Storefront, CheckCircle, ArrowsClockwise, Plug, ShieldCheck, CurrencyDollar,
  ShoppingBag, Package, UsersThree, Warning, Sparkle, LockKey, Copy, ArrowSquareOut,
  PencilSimple, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const REDIRECT_URI = `${API}/integrations/shopify/callback`;
const APP_URL = typeof window !== "undefined" ? window.location.origin : "";
const SHOPIFY_DEV_DASHBOARD = "https://dev.shopify.com/dashboard";

const FIX_STEPS = [
  "Open Shopify app scopes settings",
  "Add read_products",
  "Add read_orders",
  "Add read_customers",
  "Add read_inventory",
  "Release the app version",
  "Reinstall / re-grant the app if Shopify requires it",
  "Return to Vozelto",
  "Click Re-check scopes",
  "Click Sync now",
];

function ScopeFixChecklist({ onRecheck, onSync, recheckPending, syncPending }) {
  return (
    <div className="mt-4" data-testid="scope-fix-checklist">
      <Button
        data-testid="open-scopes-settings-btn"
        onClick={() => window.open(SHOPIFY_DEV_DASHBOARD, "_blank", "noopener")}
        className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white mb-4"
      >
        <ArrowSquareOut size={16} className="mr-1.5" weight="bold" /> Open Shopify app scopes settings
      </Button>
      <ol className="space-y-1.5">
        {FIX_STEPS.map((step, i) => {
          const num = i + 1;
          const interactive =
            (num === 1 && true) || (num === 9 && onRecheck) || (num === 10 && onSync);
          return (
            <li key={num} className="flex items-start gap-2.5 text-sm" data-testid={`fix-step-${num}`}>
              <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#6B3DFF]/15 brand-text text-[11px] font-bold flex items-center justify-center">{num}</span>
              {num === 1 ? (
                <button onClick={() => window.open(SHOPIFY_DEV_DASHBOARD, "_blank", "noopener")} className="text-left text-[#cfcfcf] hover:text-white underline decoration-dotted underline-offset-2">{step}</button>
              ) : num === 9 && onRecheck ? (
                <button data-testid="checklist-recheck-btn" onClick={onRecheck} disabled={recheckPending} className="text-left text-[#cfcfcf] hover:text-white underline decoration-dotted underline-offset-2">{recheckPending ? "Checking…" : step}</button>
              ) : num === 10 && onSync ? (
                <button data-testid="checklist-sync-btn" onClick={onSync} disabled={syncPending} className="text-left text-[#cfcfcf] hover:text-white underline decoration-dotted underline-offset-2">{syncPending ? "Syncing…" : step}</button>
              ) : (
                <span className="text-[#cfcfcf]">{step.startsWith("Add ") ? (<><span className="text-[#8a8a8a]">Add </span><code className="brand-text font-semibold">{step.slice(4)}</code></>) : step}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function ShopifyPage() {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const [domain, setDomain] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [tab, setTab] = useState("products");

  const statusQ = useQuery({ queryKey: ["shopify-status", activeId], queryFn: () => shopifyStatus(activeId), enabled: !!activeId });
  const s = statusQ.data;
  const connected = s?.connected;

  // Handle the OAuth return from Shopify (?shopify=connected | error)
  useEffect(() => {
    if (!activeId) return;
    const params = new URLSearchParams(window.location.search);
    const result = params.get("shopify");
    if (result === "connected") {
      toast.success("Shopify connected — read-only access granted.");
      qc.invalidateQueries({ queryKey: ["shopify-status", activeId] });
      window.history.replaceState({}, "", "/shopify");
    } else if (result === "error") {
      toast.error(params.get("msg") || "Shopify connection failed.");
      window.history.replaceState({}, "", "/shopify");
    }
  }, [activeId, qc]);

  const connectM = useMutation({
    mutationFn: () => shopifyOAuthStart(activeId, domain, clientId, clientSecret),
    onSuccess: (d) => { if (d?.authorize_url) window.location.href = d.authorize_url; },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not start Shopify authorization"),
  });
  const syncM = useMutation({
    mutationFn: () => shopifySync(activeId),
    onSuccess: () => { toast.success("Store synced"); qc.invalidateQueries({ queryKey: ["shopify-status", activeId] }); qc.invalidateQueries({ queryKey: ["dashboard", activeId] }); qc.invalidateQueries({ queryKey: ["shopify-data", tab, activeId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sync failed"),
  });
  const disconnectM = useMutation({
    mutationFn: () => shopifyDisconnect(activeId),
    onSuccess: () => { toast.success("Disconnected"); qc.invalidateQueries({ queryKey: ["shopify-status", activeId] }); },
  });
  const verifyM = useMutation({
    mutationFn: () => shopifyVerifyScopes(activeId),
    onSuccess: (d) => { d.ok ? toast.success("All read scopes granted 🎉 — you can Sync now.") : toast.error(`Still missing: ${d.missing_scopes.join(", ")}. Try Reconnect to mint a fresh token.`); qc.invalidateQueries({ queryKey: ["shopify-status", activeId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not verify scopes"),
  });
  const reauthM = useMutation({
    mutationFn: () => shopifyReauthorize(activeId),
    onSuccess: (d) => { if (d?.authorize_url) window.location.href = d.authorize_url; },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not start re-authorization"),
  });

  const dataQ = useQuery({
    queryKey: ["shopify-data", tab, activeId],
    queryFn: () => shopifyData(tab, activeId), enabled: !!activeId && !!connected,
  });

  const proposeM = useMutation({
    mutationFn: ({ id, type }) => shopifyPropose(activeId, id, type),
    onSuccess: () => toast.success("Proposal sent to Approval Queue"),
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const m = s?.metrics || {};

  return (
    <PageContainer>
      <PageHeader eyebrow="Integration" title="Shopify"
        subtitle="Read-only, server-to-server connection. Vozelto never writes to your store — proposed changes go to the Approval Queue for your manual approval." />

      {/* Connection card */}
      <div className={`card-matte p-6 mb-6 ${connected ? "brand-glow" : ""}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#6B3DFF]/15 flex items-center justify-center"><Storefront size={26} className="brand-text" weight="fill" /></div>
            <div>
              <div className="font-head text-lg font-semibold flex items-center gap-2">
                {connected ? s.shop_name || "Connected" : "Connect your store"}
                {connected && <span className="flex items-center gap-1 text-[11px] text-emerald-400 font-semibold"><CheckCircle size={14} weight="fill" /> Connected</span>}
              </div>
              <div className="text-sm text-[#9a9a9a]">{connected ? `${s.store_domain} · last sync ${s.last_sync ? new Date(s.last_sync).toLocaleString() : "never"}` : "Read-only Admin API · authorization-code OAuth"}</div>
            </div>
          </div>
          {connected && (
            <div className="flex gap-2">
              <Button data-testid="shopify-sync-btn" onClick={() => syncM.mutate()} disabled={syncM.isPending} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
                <ArrowsClockwise size={16} className={`mr-1.5 ${syncM.isPending ? "ai-thinking" : ""}`} /> {syncM.isPending ? "Syncing…" : "Sync now"}
              </Button>
              <Button data-testid="shopify-disconnect-btn" variant="outline" onClick={() => disconnectM.mutate()} className="border-white/15 bg-transparent hover:bg-white/5 text-white">Disconnect</Button>
            </div>
          )}
        </div>

        {!connected && (
          <div className="mt-5 grid md:grid-cols-2 gap-4 max-w-3xl">
            <div className="md:col-span-2 text-xs text-[#9a9a9a] leading-relaxed">
              Vozelto connects using Shopify's secure <span className="brand-text font-semibold">authorization-code OAuth</span> flow — read-only.
              Enter your app's Client ID & Secret, then click <span className="text-[#cfcfcf] font-semibold">Connect Shopify</span>. After connecting, Vozelto verifies the granted scopes and tells you exactly what to fix if any are missing.
            </div>
            <div>
              <Label className="label-caps">Store domain</Label>
              <Input data-testid="shopify-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="your-store.myshopify.com" className="bg-[#10101A] border-white/10 mt-1" />
            </div>
            <div>
              <Label className="label-caps">Client ID</Label>
              <Input data-testid="shopify-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Dev Dashboard → Settings → Client credentials" className="bg-[#10101A] border-white/10 mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="label-caps flex items-center gap-1"><LockKey size={12} /> Client Secret</Label>
              <Input data-testid="shopify-client-secret" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="••••••••••••••••" className="bg-[#10101A] border-white/10 mt-1" />
            </div>
            <div className="md:col-span-2 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-xs text-[#7a7a7a]"><ShieldCheck size={16} className="text-emerald-400" /> Client Secret is stored server-side only and never shown again.</div>
              <Button data-testid="shopify-connect-btn" onClick={() => connectM.mutate()} disabled={connectM.isPending || !domain || !clientId || !clientSecret} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
                <Plug size={16} className="mr-1.5" /> {connectM.isPending ? "Redirecting…" : "Connect Shopify"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* OAuth setup instructions */}
      {!connected && (
        <div className="card-matte p-6 mb-6" data-testid="shopify-scopes-guide">
          <div className="font-head text-lg font-semibold flex items-center gap-2 mb-1"><ShieldCheck size={18} className="brand-text" weight="fill" /> One-time setup — add the redirect URL</div>
          <p className="text-sm text-[#9a9a9a] mb-3">In your Shopify app config (<span className="text-[#cfcfcf]">Dev Dashboard → Black Ember AI Dashboard → Configuration → URLs → Allowed redirection URL(s)</span>), add this exact URL, then come back and click <span className="brand-text font-semibold">Connect Shopify</span>:</p>
          <div className="flex items-center gap-2 mb-4">
            <code data-testid="shopify-redirect-uri" className="flex-1 text-[12px] text-[#cfcfcf] bg-[#10101A] border border-white/10 rounded-lg px-3 py-2 break-all">{REDIRECT_URI}</code>
            <Button data-testid="copy-redirect-btn" size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(REDIRECT_URI); toast.success("Redirect URL copied"); }} className="border-white/15 bg-transparent hover:bg-white/5 text-white shrink-0"><Copy size={14} className="mr-1" /> Copy</Button>
          </div>
          <div className="font-semibold text-sm mb-2">Vozelto will request these read-only scopes on the consent screen:</div>
          <div className="grid sm:grid-cols-2 gap-2 mb-4">
            {[
              ["read_products", "Products, variants, collections, inventory levels"],
              ["read_orders", "Orders → revenue, AOV, best sellers"],
              ["read_customers", "Customers → repeat rate"],
              ["read_inventory", "Inventory quantities & low-stock alerts"],
            ].map(([scope, why]) => (
              <div key={scope} className="flex items-start gap-2 rounded-lg bg-white/[0.03] border border-white/8 px-3 py-2">
                <CheckCircle size={15} className="text-emerald-400 mt-0.5 shrink-0" weight="fill" />
                <div><code className="text-[13px] brand-text font-semibold">{scope}</code><div className="text-xs text-[#8a8a8a]">{why}</div></div>
              </div>
            ))}
          </div>
          <div className="text-xs text-[#7a7a7a] leading-relaxed">
            Vozelto stays <span className="text-emerald-400 font-semibold">100% read-only</span> — only the four read scopes above are requested and nothing is ever written to your store. Proposed changes go to the Approval Queue for your manual action.
          </div>

          <div className="mt-5 pt-5 border-t border-white/8" data-testid="shopify-managed-install-note">
            <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/25 px-3 py-3">
              <Warning size={18} className="text-amber-400 mt-0.5 shrink-0" weight="fill" />
              <div className="text-xs text-[#cfcfcf] leading-relaxed">
                <span className="font-semibold text-amber-400">Important — Managed Installation:</span> Dev Dashboard apps grant scopes from the <span className="font-semibold">released app version</span>, not the OAuth request. You must add the 4 read scopes above to your app's <span className="text-[#cfcfcf] font-semibold">API access / Configuration</span>, <span className="brand-text font-semibold">release the version</span>, then re-grant on your store. Vozelto checks this automatically and shows what's missing after you connect.
              </div>
            </div>
            <div className="mt-3 text-xs text-[#9a9a9a] leading-relaxed">
              Vozelto is a <span className="text-[#cfcfcf] font-semibold">standalone dashboard</span> — you don't open it inside Shopify. If your app's <span className="text-[#cfcfcf] font-semibold">App URL</span> shows "Example Domain", set it to this Vozelto URL (optional, just for the "Open app" link):
            </div>
            <div className="flex items-center gap-2 mt-2">
              <code data-testid="shopify-app-url" className="flex-1 text-[12px] text-[#cfcfcf] bg-[#10101A] border border-white/10 rounded-lg px-3 py-2 break-all">{APP_URL}</code>
              <Button data-testid="copy-app-url-btn" size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(APP_URL); toast.success("App URL copied"); }} className="border-white/15 bg-transparent hover:bg-white/5 text-white shrink-0"><Copy size={14} className="mr-1" /> Copy</Button>
            </div>

            <div className="mt-5 pt-5 border-t border-white/8">
              <div className="font-semibold text-sm mb-1">Fix scopes checklist</div>
              <p className="text-xs text-[#9a9a9a] mb-1">If Sync returns a 403, follow these steps to grant the read-only scopes:</p>
              <ScopeFixChecklist />
            </div>
          </div>
        </div>
      )}

      {/* Missing-scopes warning (connected but scopes not granted) */}
      {connected && (s.missing_scopes || []).length > 0 && (
        <div className="card-matte p-5 mb-6 border border-amber-500/30" data-testid="shopify-missing-scopes">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <Warning size={22} className="text-amber-400 mt-0.5 shrink-0" weight="fill" />
              <div>
                <div className="font-head font-semibold text-amber-400">Sync blocked — missing read scopes</div>
                <div className="text-sm text-[#cfcfcf] mt-1">Your app hasn't granted: <span className="font-semibold">{(s.missing_scopes || []).join(", ")}</span>.</div>
                <div className="text-xs text-[#9a9a9a] mt-2 leading-relaxed max-w-2xl">
                  Add these scopes to your app's API access in the Shopify Dev Dashboard, <span className="brand-text font-semibold">release the app version</span>, then re-grant on your store. Follow the checklist below.
                </div>
              </div>
            </div>
            <Button data-testid="shopify-verify-scopes-btn" onClick={() => verifyM.mutate()} disabled={verifyM.isPending} className="bg-amber-500 hover:bg-amber-400 text-black shrink-0">
              <ArrowsClockwise size={16} className={`mr-1.5 ${verifyM.isPending ? "ai-thinking" : ""}`} /> {verifyM.isPending ? "Checking…" : "Re-check scopes"}
            </Button>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button data-testid="shopify-reauthorize-btn" onClick={() => reauthM.mutate()} disabled={reauthM.isPending} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
              <Plug size={15} className="mr-1.5" /> {reauthM.isPending ? "Redirecting…" : "Reconnect to apply new scopes"}
            </Button>
            <span className="text-[11px] text-[#7a7a7a]">After releasing scopes in Shopify, click this to mint a fresh token (the old token's scopes are fixed).</span>
          </div>
          <ScopeFixChecklist
            onRecheck={() => verifyM.mutate()} recheckPending={verifyM.isPending}
            onSync={() => syncM.mutate()} syncPending={syncM.isPending}
          />
        </div>
      )}

      <ShopifyWriteSection activeId={activeId} />

      {connected && (
        <>
          {!s.last_sync ? (
            <div className="card-matte p-8 text-center text-[#9a9a9a]">Click <span className="brand-text font-semibold">Sync now</span> to pull your live products, orders and customers.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                <StatCard label="Revenue (90d)" value={fmtMoney(m.revenue)} icon={CurrencyDollar} accent />
                <StatCard label="Orders (90d)" value={fmtNum(m.orders)} icon={ShoppingBag} />
                <StatCard label="Avg Order Value" value={fmtMoney(m.aov)} icon={CurrencyDollar} />
                <StatCard label="Repeat Rate" value={fmtPct(m.repeat_rate)} icon={UsersThree} />
                <StatCard label="Products" value={fmtNum(m.total_products)} icon={Package} />
                <StatCard label="Customers" value={fmtNum(m.total_customers)} icon={UsersThree} />
                <StatCard label="Collections" value={fmtNum(m.total_collections)} icon={Package} />
                <StatCard label="Low Stock Alerts" value={fmtNum(m.inventory_alert_count)} icon={Warning} accent />
              </div>

              <div className="grid lg:grid-cols-2 gap-5 mb-5">
                <div className="card-matte p-5">
                  <div className="label-caps mb-3">Best Sellers (units, 90d)</div>
                  <div className="space-y-2">
                    {(s.best_sellers || []).map((b, i) => (
                      <div key={i} className="flex items-center justify-between text-sm"><span className="text-[#cfcfcf]">{b.name}</span><span className="brand-text font-semibold">{b.units} units · {fmtMoney(b.revenue)}</span></div>
                    ))}
                    {!(s.best_sellers || []).length && <div className="text-sm text-[#7a7a7a]">No order data in window.</div>}
                  </div>
                </div>
                <div className="card-matte p-5">
                  <div className="label-caps mb-3 flex items-center gap-1"><Warning size={13} className="text-amber-400" /> Inventory Alerts</div>
                  <div className="space-y-2">
                    {(s.inventory_alerts || []).map((a, i) => (
                      <div key={i} className="flex items-center justify-between text-sm"><span className="text-[#cfcfcf]">{a.name}</span><span className="text-amber-400 font-semibold">{a.inventory} left</span></div>
                    ))}
                    {!(s.inventory_alerts || []).length && <div className="text-sm text-[#7a7a7a]">All products well stocked 🎉</div>}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mb-3">
                {["products", "orders", "customers", "collections"].map((t) => (
                  <button key={t} data-testid={`shopify-tab-${t}`} onClick={() => setTab(t)}
                    className={`text-sm font-medium px-3 py-1.5 rounded-full border capitalize ${tab === t ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/12 text-white" : "border-white/10 text-[#8a8a8a] hover:text-white"}`}>{t}</button>
                ))}
              </div>

              <div className="card-matte overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
                      {tab === "products" && ["Product", "Type", "Price", "Inventory", "Status", "AI Proposal"].map((h) => <TableHead key={h} className="label-caps text-[#7a7a7a]">{h}</TableHead>)}
                      {tab === "orders" && ["Order", "Date", "Total", "Status"].map((h) => <TableHead key={h} className="label-caps text-[#7a7a7a]">{h}</TableHead>)}
                      {tab === "customers" && ["Customer ID", "Orders", "Total Spent"].map((h) => <TableHead key={h} className="label-caps text-[#7a7a7a]">{h}</TableHead>)}
                      {tab === "collections" && ["Collection", "Products"].map((h) => <TableHead key={h} className="label-caps text-[#7a7a7a]">{h}</TableHead>)}
                    </TableRow></TableHeader>
                    <TableBody>
                      {(dataQ.data || []).slice(0, 100).map((row, i) => (
                        <TableRow key={i} className="border-white/8 hover:bg-white/[0.03]">
                          {tab === "products" && <>
                            <TableCell className="text-[#cfcfcf]">{row.title}</TableCell>
                            <TableCell>{row.type}</TableCell>
                            <TableCell>{fmtMoney(row.price)}</TableCell>
                            <TableCell className={row.inventory <= 5 ? "text-amber-400" : ""}>{row.inventory}</TableCell>
                            <TableCell className="capitalize">{row.status}</TableCell>
                            <TableCell>
                              <Button data-testid="propose-seo-btn" size="sm" variant="ghost" onClick={() => proposeM.mutate({ id: row.shopify_id, type: "seo_title" })} className="h-7 text-[#6B3DFF] hover:text-[#A56CFF] hover:bg-[#6B3DFF]/10"><Sparkle size={13} className="mr-1" weight="fill" /> SEO title</Button>
                            </TableCell>
                          </>}
                          {tab === "orders" && <>
                            <TableCell className="text-[#cfcfcf]">{row.name}</TableCell>
                            <TableCell>{(row.created_at || "").slice(0, 10)}</TableCell>
                            <TableCell>{fmtMoney(row.total_price)}</TableCell>
                            <TableCell className="capitalize">{row.financial_status}</TableCell>
                          </>}
                          {tab === "customers" && <>
                            <TableCell className="text-[#cfcfcf]">{row.shopify_id}</TableCell>
                            <TableCell>{row.orders_count}</TableCell>
                            <TableCell>{fmtMoney(row.total_spent)}</TableCell>
                          </>}
                          {tab === "collections" && <>
                            <TableCell className="text-[#cfcfcf]">{row.title}</TableCell>
                            <TableCell>{row.products_count}</TableCell>
                          </>}
                        </TableRow>
                      ))}
                      {dataQ.data && dataQ.data.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#7a7a7a]">No {tab} found.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </PageContainer>
  );
}

const WRITE_ACTIONS = [
  ["create_product", "Create Product"], ["update_product", "Update Product"],
  ["update_description", "Update Description"], ["update_images", "Update Images"],
  ["update_pricing", "Update Pricing"], ["update_seo", "Update SEO"],
  ["update_collections", "Update Collections"], ["update_tags", "Update Tags"],
];

function ShopifyWriteSection({ activeId }) {
  const [action, setAction] = useState("update_description");
  const [productId, setProductId] = useState("");
  const [productTitle, setProductTitle] = useState("");
  const [value, setValue] = useState("");

  const activityQ = useQuery({ queryKey: ["shopify-activity", activeId], queryFn: () => shopifyWriteActivity(activeId), enabled: !!activeId });

  const buildPayload = () => {
    if (action === "update_description") return { body_html: value };
    if (action === "update_seo") return { seo_title: value };
    if (action === "update_tags" || action === "update_collections") return { tags: value };
    if (action === "update_pricing") return { variants: [{ price: value }] };
    if (action === "create_product") return { title: productTitle || value, body_html: value };
    return { value };
  };

  const proposeM = useMutation({
    mutationFn: () => shopifyWritePropose({
      client_id: activeId, action,
      shopify_product_id: productId ? parseInt(productId, 10) : null,
      product_title: productTitle, payload: buildPayload(), summary: value.slice(0, 200),
    }),
    onSuccess: () => { toast.success("Change sent to Approval Queue — approve it to apply to Shopify"); setValue(""); setProductId(""); setProductTitle(""); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not propose change"),
  });

  const needsProduct = action !== "create_product";
  const canSubmit = value.trim() && (!needsProduct || productId.trim());

  return (
    <div className="card-matte p-6 mb-6" data-testid="shopify-write-section">
      <div className="font-head text-lg font-semibold flex items-center gap-2 mb-1"><PencilSimple size={18} className="brand-text" weight="fill" /> Approval-based Shopify changes</div>
      <p className="text-sm text-[#9a9a9a] mb-4">Prepare a store change here. It goes to the <span className="text-[#cfcfcf] font-semibold">Approval Queue</span> and is only written to Shopify after you manually approve and click <span className="brand-text font-semibold">Apply to Shopify</span>. Nothing publishes automatically.</p>

      <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
        <div>
          <Label className="label-caps">Action</Label>
          <select data-testid="write-action-select" value={action} onChange={(e) => setAction(e.target.value)} className="w-full bg-[#10101A] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#cfcfcf] mt-1">
            {WRITE_ACTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-caps">Product title</Label>
          <Input data-testid="write-product-title" value={productTitle} onChange={(e) => setProductTitle(e.target.value)} placeholder="e.g. Dark Roast" className="bg-[#10101A] border-white/10 mt-1" />
        </div>
        {needsProduct && (
          <div>
            <Label className="label-caps">Shopify Product ID</Label>
            <Input data-testid="write-product-id" value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="Required for updates" className="bg-[#10101A] border-white/10 mt-1" />
          </div>
        )}
        <div className="md:col-span-2">
          <Label className="label-caps">New value / content</Label>
          <Textarea data-testid="write-value" value={value} onChange={(e) => setValue(e.target.value)} rows={3} placeholder="The new description, price, tags, SEO title…" className="bg-[#10101A] border-white/10 mt-1" />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <Button data-testid="write-propose-btn" onClick={() => proposeM.mutate()} disabled={!canSubmit || proposeM.isPending} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
          <ShieldCheck size={16} className="mr-1.5" /> Send to Approval Queue
        </Button>
        <span className="text-xs text-[#7a7a7a]">Two-step gate: propose → approve → apply.</span>
      </div>

      <div className="mt-6 pt-5 border-t border-white/8">
        <div className="label-caps mb-3 flex items-center gap-1"><ClockCounterClockwise size={13} className="brand-text" /> Activity Log</div>
        {!(activityQ.data || []).length ? (
          <div className="text-sm text-[#7a7a7a]">No changes applied yet. Approved changes appear here once applied.</div>
        ) : (
          <Table>
            <TableHeader><TableRow className="border-white/10 hover:bg-transparent">{["When", "Action", "Product", "Result"].map((h) => <TableHead key={h} className="label-caps text-[#7a7a7a]">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{(activityQ.data || []).map((a) => (
              <TableRow key={a.id} data-testid={`activity-${a.id}`} className="border-white/8 hover:bg-white/[0.03]">
                <TableCell className="text-[#9a9a9a]">{new Date(a.at).toLocaleString()}</TableCell>
                <TableCell className="text-[#cfcfcf]">{a.action_label}</TableCell>
                <TableCell>{a.product_title || a.product_id || "—"}</TableCell>
                <TableCell><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${a.status === "applied" ? "bg-emerald-500/15 text-emerald-400" : a.status === "failed" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-300"}`}>{a.status}</span></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
